import { useState } from "react";
import { ChevronDown, Info } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";

/**
 * «Cosa cambia scegliendo uno sport», spiegato dove lo sport si sceglie.
 *
 * Due pubblici, un componente:
 *  - `athlete`: cosa cambia per un atleta. Lo vede l'atleta nel Profilo e lo
 *    vede **identico** il coach nella scheda atleta (`forCoach`), così può
 *    spiegarlo e impostarlo al posto suo con le stesse parole;
 *  - `coach`: cosa cambia per un coach seguire una disciplina. Oggi lo legge
 *    l'admin dove le assegna (Gestione Coach); con la sezione coach della
 *    revisione sport lo leggerà il coach stesso.
 *
 * Chiuso di default: serve la prima volta, non a ogni apertura.
 * Il testo descrive il comportamento **verificato nel codice**: se cambia cosa
 * governa uno sport, va aggiornato qui. Spec: docs/sport-config/ANALYSIS.md §1.3
 */
const ROWS = {
	athlete: [
		["sportsExplainProfile", "sportsExplainProfileBody"],
		["sportsExplainLeaderboards", "sportsExplainLeaderboardsBody"],
		["sportsExplainSections", "sportsExplainSectionsBody"],
		["sportsExplainCompetitions", "sportsExplainCompetitionsBody"],
		["sportsExplainPrimary", "sportsExplainPrimaryBody"],
		["sportsExplainUnchanged", "sportsExplainUnchangedBody"],
	],
	coach: [
		["coachDisciplinesExplainWhat", "coachDisciplinesExplainWhatBody"],
		["coachDisciplinesExplainScope", "coachDisciplinesExplainScopeBody"],
		["coachDisciplinesExplainEmpty", "coachDisciplinesExplainEmptyBody"],
		["coachDisciplinesExplainData", "coachDisciplinesExplainDataBody"],
		["coachDisciplinesExplainConfigure", "coachDisciplinesExplainConfigureBody"],
	],
};

const TITLE = {
	athlete: "sportsExplainTitle",
	coach: "coachDisciplinesExplainTitle",
};

export function SportsExplainer({ audience = "athlete", forCoach = false, defaultOpen = false }) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(defaultOpen);
	const rows = ROWS[audience] || ROWS.athlete;

	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger asChild>
				<button
					type="button"
					className="flex w-full items-center gap-1.5 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
				>
					<Info className="w-3.5 h-3.5 shrink-0 text-volt" />
					<span className="flex-1">{t(TITLE[audience] || TITLE.athlete)}</span>
					<ChevronDown
						className={`w-3.5 h-3.5 shrink-0 transition-transform ${
							open ? "rotate-180" : ""
						}`}
					/>
				</button>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<dl className="mt-2 space-y-2 rounded-sm border border-border/60 bg-secondary/30 p-3">
					{rows.map(([titleKey, bodyKey]) => (
						<div key={titleKey}>
							<dt className="text-[11px] font-medium uppercase tracking-wider text-volt">
								{t(titleKey)}
							</dt>
							<dd className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
								{t(bodyKey)}
							</dd>
						</div>
					))}
					{forCoach && audience === "athlete" && (
						<p className="text-xs text-foreground/80 border-t border-border/60 pt-2">
							{t("sportsExplainCoachNote")}
						</p>
					)}
				</dl>
			</CollapsibleContent>
		</Collapsible>
	);
}
